import api from 'api';
import * as summaryApi from 'api/users/summary.api';
import type { LoadingStatus } from 'components/users/flexlist/FlexList';
import { ERROR } from 'components/users/flexlist/FlexList';
import {
  USERS_DASHBOARD_INCLUDE_PLANNED_TIME,
  USERS_DASHBOARD_INCLUDE_TASKED_TIME,
} from 'constants/user';
import { errorMessageFactory } from 'factories/api';
import { zip } from 'lodash-es';
import { getIsDemoManagerPresetActive } from 'redux/modules/tic/tic.demo.selectors';
import { showToast } from 'redux/modules/toasts.actions';
import { saveUserProperty } from 'redux/modules/user.actions';
import {
  getDashboardUserIdsToLoad,
  getDashboardXhrStatus,
  getGraphQueryParamsFromFilter,
  getRange,
  getSelectedUserIds,
  getUsersDashboard,
  getUsersDashboardFilter,
  getUsersListQueryParamsFromFilter,
  getWeeksByUser,
  isNoneUsersOrTeamsSelected,
} from 'redux/modules/users-page/users-dashboard/usersDashboard.selectors';
import type {
  Filter,
  UsersSummaryQuery,
} from 'redux/modules/users-page/users-dashboard/usersDashboard.types';
import {
  fetchedGraphTotals,
  fetchedUserSummaries,
  resetGraphTotals,
  setFilter,
  setUserSummariesLoadingStatus,
} from 'redux/modules/users-page/users-dashboard/usersDashboard.types';
import { createXhrStatusModule, xhrStatusQueryInfo } from 'redux/reusable/xhrStatus';
import { getUserEntities } from 'redux/selectors/users';
import type { Thunk } from 'types/redux';

// "Sleeping" & "Burning" filters aren't managed by InfiniteUserList's pagination
// so we track page size manually instead
const MANUAL_FETCHING_LIMIT = 50;
const FETCH_MAX_AGE_IN_MS = 300000;
const { wrapFetching } = createXhrStatusModule('usersDashboard');

export const fetchGraphTotalsForFilter = (): Thunk<Promise<void>> => {
  return async (dispatch, getState) => {
    const state = getState();
    if (getIsDemoManagerPresetActive(state)) {
      return;
    }
    const filter = getUsersDashboardFilter(state);

    if (isNoneUsersOrTeamsSelected(filter)) {
      dispatch(resetGraphTotals());
    } else {
      const queryParamsFromFilter = getGraphQueryParamsFromFilter(state);
      const totals = await dispatch(
        wrapFetching(summaryApi.getUsersSummaryTotals(queryParamsFromFilter), 'graph')
      );
      dispatch(fetchedGraphTotals(totals));
    }
  };
};

// this will reset the dropdown with filterable users (called when adding user)
export const resetQueryUsersInDashboard = (): Thunk => {
  return dispatch => {
    // Reset to no filter (all users)
    return dispatch(setFilter({ userIds: undefined, teamIds: undefined }));
  };
};

function getGroupUserIdsAsync(
  filter: Filter,
  since: string,
  until: string
): Thunk<Promise<number[]>> {
  return async (dispatch, getState) => {
    let isDone = false;
    let page = 1;
    const userIds: Array<number> = [];

    const { filteredUsers, filteredTeams } = getUsersDashboard(getState());

    while (!isDone) {
      const result = await summaryApi.getUsersSummaryGrouped({
        since,
        until,
        userIds: filteredUsers,
        teamIds: filteredTeams,
        filter: filter.mode,
        percent: filter.percent,
        timeframe_filter_method: filter.timeframe_filter_method,
        totals: false,
        groupBy: 'week',
        page,
        perPage: 15,
        hasAccessToTasks: false, // Not needed in this context
      });

      // NOTE: need to check this since backend can return either array or object
      if (Array.isArray(result)) {
        userIds.push(...result.map(u => u.id));
        if (result.length <= 5) {
          isDone = true;
        } else {
          page = page + 1;
        }
      }
    }

    return Promise.resolve(userIds);
  };
}

export const updateFilter = (filter: Partial<Filter & { shouldReset?: boolean }>): Thunk => {
  return async (dispatch, getState) => {
    const state = getState();
    const filterState = getUsersDashboardFilter(state);
    dispatch(setFilter(filter));

    if (isNoneUsersOrTeamsSelected({ ...filterState, ...filter })) {
      dispatch(resetGraphTotals());
      return undefined;
    }
  };
};

export const fetchUsers = (): Thunk => {
  return async (dispatch, getState) => {
    const state = getState();
    const filter = getUsersDashboardFilter(state);

    const { userIds } = filter;

    if (Array.isArray(userIds) && userIds.length > 0) {
      const weeksByUser = getWeeksByUser(state);

      const idsToFetch = userIds.filter(u => {
        const hasUser = weeksByUser[u];

        if (!hasUser || !hasUser.lastFetch) return false;

        return hasUser.lastFetch - Date.now() > FETCH_MAX_AGE_IN_MS;
      });

      if (idsToFetch.length > 0) {
        dispatch(fetchUsersWithWeekSummaryForFilter({ userIds: idsToFetch }));
      }
    } else {
      dispatch(fetchUsersWithWeekSummaryForFilter());
    }
  };
};

export const sendLogHoursNotificationToUser = (userId: number): Thunk => {
  return async (dispatch, getState) => {
    if (getIsDemoManagerPresetActive(getState())) {
      return;
    }
    try {
      const userEntities = getUserEntities(getState());
      const myUser = userEntities[userId];

      await api.sendLogHoursRequestNotification(userId);

      dispatch(
        showToast({
          message: `${myUser.name} was notified to log their hours`,
          type: 'success',
          title: 'Notification sent',
        })
      );
    } catch (err) {
      dispatch(
        showToast({
          message: `An error occurred when sending notification to log hours: ${err.message}`,
          type: 'danger',
          title: 'Oh no!',
        })
      );
      throw err;
    }
  };
};

export const sendNotificationToGroup = (): Thunk => {
  return async (dispatch, getState) => {
    if (getIsDemoManagerPresetActive(getState())) {
      return;
    }
    try {
      const state = getState();
      const filter = getUsersDashboardFilter(state);
      const selectedUsers = getSelectedUserIds(state);
      const range = getRange(state);

      const sanitizedFilter = {
        ...filter,
        userIds: selectedUsers,
      };

      const userIds = await dispatch(
        getGroupUserIdsAsync(sanitizedFilter, range.since, range.until)
      );
      await api.sendLogHoursRequestNotification(userIds);

      dispatch(
        showToast({
          message: `${userIds.length} users were notified to log their hours`,
          type: 'success',
          title: 'Notification sent',
        })
      );
    } catch (err) {
      dispatch(
        showToast({
          message: `An error occurred when sending notifications to log hours: ${err.message}`,
          type: 'danger',
          title: 'Oh no!',
        })
      );
      throw err;
    }
  };
};
export const fetchMoreUsersWithSummary =
  (userIdsToLoad: Array<number>): Thunk<Promise<unknown>> =>
  async (dispatch, getState) => {
    const state = getState();
    const queryParamsFromFilter = getUsersListQueryParamsFromFilter(state);
    const overrides: Omit<UsersSummaryQuery, 'hasAccessToTasks'> = {
      userIds: userIdsToLoad,
      teamIds: undefined,
      page: 1,
    };
    const queryParams: UsersSummaryQuery = { ...queryParamsFromFilter, ...overrides };
    const xhrStatus = xhrStatusQueryInfo(getDashboardXhrStatus(state), { key: 'users' });

    if (xhrStatus.isInFlight || !queryParams.userIds?.length) {
      return;
    }

    return dispatch(getSummariesSequentially(queryParams, true));
  };

export const getSummariesSequentially =
  (queryParams: UsersSummaryQuery, isVirtualizedFetch = false): Thunk<Promise<unknown>> =>
  async (dispatch, getState) => {
    if (getIsDemoManagerPresetActive(getState())) {
      return undefined;
    }
    try {
      const summaries = (await dispatch(
        wrapFetching(summaryApi.getUsersSummaryGrouped(queryParams), 'users')
      )) as Array<summaryApi.UserSummaryDefaultGroupedPayload>;

      const usersWithSummaries = zip(summaries).map(([summary]) =>
        summaryApi.mergeSummariesWithGroups(summary)
      );

      const pageSize = queryParams.perPage ?? MANUAL_FETCHING_LIMIT;
      dispatch(
        fetchedUserSummaries({
          result: { users: usersWithSummaries },
          params: queryParams,
          maybeHasMore: usersWithSummaries.length === pageSize,
          isVirtualizedFetch,
        })
      );

      if (queryParams.hasAccessToTasks) {
        const forecastSummaries = (await dispatch(
          wrapFetching(summaryApi.getUsersSummaryGrouped(queryParams, 'forecasts'), 'users')
        )) as Array<summaryApi.UserSummaryForecastGroupedPayload>;

        const usersWithForecastSummaries = zip(summaries, forecastSummaries!).map(
          ([summary, forecastSummary]) =>
            summaryApi.mergeSummariesWithGroups(summary, forecastSummary)
        );

        dispatch(
          fetchedUserSummaries({
            result: { users: usersWithForecastSummaries },
            params: queryParams,
            scope: 'forecasts',
            maybeHasMore: usersWithForecastSummaries.length === pageSize,
            isVirtualizedFetch,
          })
        );
        return usersWithForecastSummaries;
      }
    } catch (err) {
      const message = errorMessageFactory(err);

      if (queryParams.userIds?.length) {
        if (Array.isArray(queryParams.userIds)) {
          const usersWithError: Record<string, LoadingStatus> = queryParams.userIds.reduce(
            (users: Record<string, LoadingStatus>, userId: number) => {
              users[userId] = ERROR;
              return users;
            },
            {} as Record<string, LoadingStatus>
          );

          dispatch(setUserSummariesLoadingStatus(usersWithError));
        }
      }

      dispatch(
        showToast({
          message,
          type: 'danger',
        })
      );
      throw err;
    }
  };

export const fetchUsersWithWeekSummaryForFilter =
  (filter?: Partial<Filter & { shouldReset?: boolean }>): Thunk<Promise<unknown>> =>
  async (dispatch, getState) => {
    const state = getState();
    const queryParamsFromFilter = getUsersListQueryParamsFromFilter(state);
    const updatedFilter = filter ? { ...queryParamsFromFilter, ...filter } : queryParamsFromFilter;

    return dispatch(getSummariesSequentially(updatedFilter));
  };

/**
 * Loads the next batch of dashboard user summaries.
 *
 * Default view: the full directory is rendered and summaries are loaded by id
 * (the next page of not-yet-loaded users), so the list is always covered
 * regardless of how the backend paginates or orders `/users/summary`. This
 * replaces the page-number pagination that stopped at the first short page and
 * dropped a range of users from the list (they stayed only in the dropdown).
 * Sleeping/burning modes keep the API's page pagination — those are inherently
 * filtered result sets. (TT-5478)
 */
export const fetchDashboardUsersBatch = (): Thunk<Promise<unknown>> => {
  return async (dispatch, getState) => {
    const state = getState();
    const filter = getUsersDashboardFilter(state);

    if (filter.mode) {
      return dispatch(fetchUsersWithWeekSummaryForFilter());
    }

    const idsToLoad = getDashboardUserIdsToLoad(state);
    if (idsToLoad.length === 0) {
      return undefined;
    }
    return dispatch(fetchMoreUsersWithSummary(idsToLoad));
  };
};

export const setUsersDashboardIncludePlannedTime = (includePlannedTime: boolean): Thunk => {
  return dispatch => {
    dispatch(saveUserProperty(USERS_DASHBOARD_INCLUDE_PLANNED_TIME, includePlannedTime));
  };
};

export const setUsersDashboardIncludeTaskedTime = (includeTaskedTime: boolean): Thunk => {
  return dispatch => {
    dispatch(saveUserProperty(USERS_DASHBOARD_INCLUDE_TASKED_TIME, includeTaskedTime));
  };
};
